import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { BaseApiService } from './base-api-service';
import { BusyService } from './busy-service';
import { PaginationInfo, PaginationParams } from '../types/common';


// 🧾 Request and response models

export interface SalesOrderLine {
  product_id: number;
  product_uom_qty: number;
  price_unit: number;
}

export interface CreateSalesOrderPayload {
  partner_id: number;
  partner_client_uuid?: string;
  idempotencyKey: string;
  order_line: SalesOrderLine[];
}

export interface SalesOrderResponse {
  status: string;
  message: string;
  order_id?: number;
  state?: string;
}

// 📥 Filter request payload
export interface FilterSalesOrderPayload extends PaginationParams {
  startDate: string;
  endDate: string;
  customerId?: number;
  includeTotals?: boolean;
}

// 📤 Single order returned by filter
export interface FilteredSalesOrder {
  order_id: number;
  name: string;
  date_order: string;
  state: string;
  user_id: number | null;
  user_name: string | null;
  totalAmount: number;
  invoiceStatus: string;
  dateOrder: Date| null;
  customer_name: string | null;
}



// 📤 Full filter response from API
export interface FilterSalesOrderResponse {
  status: 'success' | 'error';
  orders: FilteredSalesOrder[];
  grandTotal?: number;
  pagination?: PaginationInfo;
  message?: string;
}

export interface SalesOrderLineDetail {
  product_id: number;
  quantity: number;
  price_unit: number;
}

export interface SalesOrderDetail {
  order_id: number;
  name: string;
  dateOrder: string | null;
  state: string;
  user_id: number | null;
  user_name: string | null;
  totalAmount: number;
  invoiceStatus: string;
  customerId: number | null;
  lines: SalesOrderLineDetail[];
}

export interface GetSalesOrderByIdResponse {
  status: 'success' | 'error';
  order?: SalesOrderDetail;
  message?: string;
}


@Injectable({
  providedIn: 'root'
})
export class SalesOrderService extends BaseApiService {
  constructor(http: HttpClient, busy: BusyService) {
    super(http, busy);
  }

  /**
   * Create a new sales order
   */
  createOrder(payload: CreateSalesOrderPayload): Observable<SalesOrderResponse> {
    return this.post<SalesOrderResponse>('sales/order', payload);
  }

  /**
   * Check order existence by idempotency key
   */
  checkOrder(payload: { idempotencyKey: string }): Observable<SalesOrderResponse> {
    return this.post<SalesOrderResponse>('sales/order/check', payload);
  }

  /**
   * Filter orders between startDate and endDate (with optional pagination & totals)
   */
  filterOrders(payload: FilterSalesOrderPayload): Observable<FilterSalesOrderResponse> {
    return this.post<FilterSalesOrderResponse>('sales/order/filter', payload);
  }

  /**
   * Get sales order details by ID
   */
  getSalesOrderById(orderId: number): Observable<GetSalesOrderByIdResponse> {
    return this.get<GetSalesOrderByIdResponse>(`sales/order/${orderId}`);
  }

  /** Get delivered from a given Sales Order ID */
  getDeliveredBySalesOrderId(orderId: number): Observable<GetSalesOrderByIdResponse> {
    // Backend endpoint: /v1/api/sales/order/:order_id/invoices
    return this.get<GetSalesOrderByIdResponse>(`sales/order/${orderId}/invoices`);
  }
}
